/**
 * Network Status Bridge — module-level trigger connecting the axios response
 * interceptor (`services/api.js`) to the connectivity monitor
 * (`services/connectivityMonitor.js`).
 *
 * Why a module bridge instead of React context:
 * - The axios interceptor lives outside React and cannot call hooks.
 * - Same module-store pattern as `hooks/usePrivacyMode.js`.
 *
 * Responsibilities are deliberately minimal now — the monitor owns the
 * dedupe (it is idempotent, so a storm of parallel failures starts one loop)
 * and the recovery (it stops itself on the first successful ping):
 * - `notifyNetworkError()` — a genuine failure happened (ERR_NETWORK only,
 *   never aborted/canceled requests): invoke the registered handler, which
 *   starts the connectivity monitor and the app-wide reconnecting overlay.
 * - `notifyRequestSuccess()` — any successful response is strong evidence the
 *   server is back: if the monitor is running, kick an immediate check
 *   instead of waiting for the next 3s tick.
 */

import {
  isConnectivityMonitorActive,
  retryConnectivityNow,
} from "services/connectivityMonitor";

let networkErrorHandler = null;

/**
 * Registers the UI handler that starts the connectivity monitor.
 * Called by `NetworkErrorListener` on mount. Non-function values are ignored.
 */
export const setNetworkErrorHandler = (fn) => {
  networkErrorHandler = typeof fn === "function" ? fn : null;
};

/**
 * Removes the registered handler. Called by `NetworkErrorListener` on unmount.
 */
export const clearNetworkErrorHandler = () => {
  networkErrorHandler = null;
};

/**
 * Called by the axios interceptor for genuine network failures
 * (ERR_NETWORK only — never aborted/canceled requests).
 *
 * Safe no-op when no handler is registered yet (e.g. very early requests on a
 * cold load, before `NetworkErrorListener` mounts) — `AuthContext` covers
 * that case by starting the monitor itself after its retries are exhausted.
 */
export const notifyNetworkError = () => {
  if (typeof networkErrorHandler !== "function") return;

  try {
    networkErrorHandler();
  } catch (err) {
    // A UI failure must never break request error handling.
    console.warn("NetworkStatus: error handler threw:", err);
  }
};

/**
 * Called on every successful response — we are back online / the server is
 * reachable again. Only meaningful while a reconnect episode is on screen:
 * it asks the monitor for an immediate check so the overlay clears as soon as
 * a real request succeeds. A no-op otherwise.
 */
export const notifyRequestSuccess = () => {
  if (isConnectivityMonitorActive()) {
    retryConnectivityNow();
  }
};

/**
 * Kept for `ContextProvider.closeDialog` compatibility. The old
 * "No Internet Connection" dialog (and its notice lock/cooldown) no longer
 * exists — the reconnecting overlay owns that UX now — so there is no notice
 * state left to clear. Retained (instead of deleted) so the dialog context,
 * which is outside this change's scope, keeps compiling unchanged.
 */
export const clearNetworkNotice = () => {};

/**
 * Live getter for the "server outage on screen" flag, read by
 * `ContextProvider.openDialog` inside its functional updater so a stale
 * `openDialog` reference captured before the outage still sees the CURRENT
 * state. True while the connectivity monitor is running — i.e. while the
 * reconnecting overlay is up — so routine scene error dialogs
 * ("Unable to connect…") are suppressed instead of piling up behind it.
 */
export const isServerOutageActive = () => isConnectivityMonitorActive();
